import {Group} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import ArchiveRecordLabel from 'archive/components/ArchiveRecordLabel';
import getArchiveAwareDisplayName from 'archive/getArchiveAwareDisplayName';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  Container,
  Grid,
  Location,
  Plate,
  Storable,
  StorableSummary,
  StorableSummaryExtras,
} from 'constants/SampleConstants';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import {StorableValidationInfoById} from 'inventory/location-validation/ParentStorableValidationUtils';
import StorableValidationStatusIcon from 'inventory/location-validation/StorableValidationStatusIcon';
import MissingRequiredFieldsIcon from 'inventory/required-fields/MissingRequiredFieldsIcon';
import {getMissingRequiredFields} from 'inventory/required-fields/missingRequiredFieldsUtil';
import {getRestrictedSampleUsersHeaderText} from 'inventory/sample-status/RestrictedSampleUsersTextUtil';
import {SampleControlsFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevel';
import {hasMinimumSCRFeatureLevel} from 'inventory/sample-status/SampleControlsFeatureLevelUtil';
import {QueryingSampleRestrictionStatusLabel} from 'inventory/sample-status/SampleRestrictionStatusLabel';
import SampleStatusLabel from 'inventory/sample-status/SampleStatusLabel';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import StarIcon from 'nav/sidepanel/StarIcon';
import ContainerConcentration from 'samples/containers/ContainerConcentration';
import {ExperimentalRolesByContainerId} from 'samples/containers/ExperimentalRoleUtils';
import SampleControlRoleUsers from 'samples/containers/SampleRoleUsers';
import {ContainerValidContentsInfoById} from 'samples/containers/ValidContentsUtils';
import RoleDisplay from 'samples/experimental-roles/ExperimentalRoleDisplay';
import getQuantityDisplayString from 'samples/getQuantityDisplayString';
import {isGridSummary} from 'samples/SampleUtil';
import * as StorableContextMenu from 'samples/sidepanel/StorableContextMenu';
import * as StorableActions from 'samples/StorableActions';
import AncestralStorables from 'samples/storables/AncestralStorables';
import {PLATE_SCHEMA_TYPE_TO_TEXT, PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import StorableTagValue from 'samples/storables/StorableTagValue';
import * as StorableSummaryExtrasColumns from 'samples/StorableSummaryExtras';
import {Measurement} from 'samples/transfers/Measurements';
import ExpirationDateCell from 'samples/valid-contents/ExpirationDateCell';
import FreezeThawCountCell from 'samples/valid-contents/FreezeThawCountCell';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {TagSchemaSummaryWithFieldsGraphQL} from 'schemas/useTagSchemaSummariesWithFields';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import {
  getStudiesRowContentHeight,
  maybeGetStudiesListingColumn,
  StudyableItemListingExtras,
} from 'studies/studyable/StudiesListingColumn';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ScrollableTableColumn, TABLE_ROW_BODY_HEIGHT} from 'ui-library/scrollable/ScrollableTableConstants';
import APIUtil, {apiIdToIconName} from 'util/APIUtil';
import * as DateUtil from 'util/DateUtil';
import {BasicEvent} from 'util/Types';
import {maybeAddWorklistPositionColumn} from 'worklists/sidepanel-components/WorklistItemsSidepanel';

export interface StorableListingExtras extends StudyableItemListingExtras {
  storableSummaryExtras?: StorableSummaryExtras | null;
  containerValidContentsInfoById?: ContainerValidContentsInfoById;
  selectedContainableTagSchemas?: Array<TagSchemaSummaryWithFieldsGraphQL>;
  experimentalRolesByContainerId?: ExperimentalRolesByContainerId;
  storableValidationInfoById?: StorableValidationInfoById;
}

export type StorableColumnExtras = Pick<
  StorableListingExtras,
  'storableSummaryExtras' | 'selectedContainableTagSchemas'
>;

export default class StorableListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.STORABLE> {
  listingRendererType = ListingRendererType.STORABLE;

  renderIcon(storable: Storable): JSX.Element {
    return <BenchlingIcon className="scrollableTable-cell-icon" icon={apiIdToIconName(storable.id)} />;
  }

  private handleClickStar = (storable: Storable): void => {
    if (storable.starred) {
      StorableActions.unstarStorables([storable.id]);
    } else {
      StorableActions.starStorables([storable.id]);
    }
  };

  getNonPluginColumnConfigurations(
    storables: Array<Storable>,
    {
      storableSummaryExtras,
      selectedContainableTagSchemas,
      itemStudyAttributes,
      containerValidContentsInfoById,
      experimentalRolesByContainerId,
      storableValidationInfoById,
    }: StorableListingExtras = {
      storableSummaryExtras: null,
    }
  ): Array<ColumnConfiguration<Storable>> {
    const maybeStudiesColumn = maybeGetStudiesListingColumn<Storable>(itemStudyAttributes);

    let columns: Array<ColumnConfiguration<StorableSummary>> = [
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_BARCODE_KEY),
          accessorKey: ColumnAccessorKey.ID,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_BARCODE_KEY,
          width: 200,
        },
        render: (storable: Storable) => (
          <div className={css(styles.barcodeCellContainer)}>
            <span className={css(styles.barcodeCellBarcode)}>{storable.barcode || 'No barcode'}</span>
            <Group spacing="xs" position="right" noWrap={true} miw={60} sx={{flexShrink: 0}}>
              <SampleStatusLabel
                item={{...storable, ...containerValidContentsInfoById?.[storable.id]}}
                includeAvailability={true}
                includeAuthorization={hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1)}
                includeExpiration={BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE}
                renderAllStatuses={false}
                includeMessage={false}
              />
              <StorableValidationStatusIcon
                storable={{...storable, ...storableValidationInfoById?.[storable.id]}}
              />
              <MissingRequiredFieldsIcon missingRequiredFields={getMissingRequiredFields(storable)} />
            </Group>
          </div>
        ),
      },
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_STARRED),
          accessorKey: ColumnAccessorKey.STAR,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_STARRED,
          width: 70,
        },
        render: (storable: Storable) => (
          <StarIcon
            onClick={() => {
              this.handleClickStar(storable);
            }}
            starred={storable.starred}
          />
        ),
      },
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_NAME_KEY),
          accessorKey: ColumnAccessorKey.NAME,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
          isFixed: true,
          width: 120,
        },
        render: (storable: Storable) => (
          <Tooltip title={storable.name} container="body" placement={TooltipPlacement.TOP} delay={350}>
            <span className="u-ellipsis-text">
              {storable.name}
              <ArchiveRecordLabel item={storable} />
            </span>
          </Tooltip>
        ),
      },
      {
        column: {
          header: 'Location',
          accessorKey: ColumnAccessorKey.PARENT_STORABLE,
          flexGrow: 1,
          isCollapsible: false,
          isSortable: false,
          width: 120,
        },
        render: (storable: Storable) => (
          <AncestralStorables storableId={storable.id} noLocationText="None" showGridCell={true} />
        ),
      },
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          accessorKey: ColumnAccessorKey.CREATED_AT,
          flexGrow: 1,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
          width: 120,
        },
        render: (storable: Storable) => (
          <Tooltip
            title={DateUtil.readableTime(storable.createdAt, {showTime: true})}
            container="body"
            placement={TooltipPlacement.TOP}
            delay={350}
          >
            <span className="u-ellipsis-text">
              {DateUtil.readableTime(storable.createdAt, {showTime: false})}
            </span>
          </Tooltip>
        ),
      },
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          flexGrow: 1,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
          width: 120,
        },
        render: (storable: Storable) => (
          <Tooltip
            title={FileBrowserUtil.getResultDate(storable, {showTime: true}).date}
            container="body"
            placement={TooltipPlacement.TOP}
            delay={350}
          >
            <span className="u-ellipsis-text">
              {FileBrowserUtil.getResultDate(storable, {showTime: false}).date}
            </span>
          </Tooltip>
        ),
      },
      ...(BenchlingConfig.INVENTORY_ENABLE_EXPIRATION_DATE
        ? [
            {
              column: {
                header: getColumnHeaderFromSortKey(SortKey.SORT_EXPIRATION_DATE),
                accessorKey: ColumnAccessorKey.EXPIRATION_DATE,
                isCollapsible: true,
                isSortable: true,
                sortKey: SortKey.SORT_EXPIRATION_DATE,
              },
              render: (storable: StorableSummary) => (
                <ExpirationDateCell
                  expirationInfo={containerValidContentsInfoById?.[storable.id].expirationInfo}
                />
              ),
              isHidden: (storable: StorableSummary) =>
                !APIUtil.isContainer(storable.id) || containerValidContentsInfoById?.[storable.id] == null,
            },
          ]
        : []),
      ...(BenchlingConfig.INVENTORY_ENABLE_FREEZE_THAW
        ? [
            {
              column: {
                header: getColumnHeaderFromSortKey(SortKey.SORT_FREEZE_THAW_COUNT),
                accessorKey: ColumnAccessorKey.FREEZE_THAW_COUNT,
                isCollapsible: true,
                isSortable: true,
                sortKey: SortKey.SORT_FREEZE_THAW_COUNT,
              },
              render: (storable: StorableSummary) => (
                <FreezeThawCountCell
                  freezeThawInfo={containerValidContentsInfoById?.[storable.id].freezeThawInfo}
                />
              ),
              isHidden: (storable: StorableSummary) =>
                !APIUtil.isContainer(storable.id) || containerValidContentsInfoById?.[storable.id] == null,
            },
          ]
        : []),
      ...(maybeStudiesColumn ? [maybeStudiesColumn] : []),
      {
        column: {
          header: getColumnHeaderFromSortKey(SortKey.SORT_SCHEMA_NAME),
          accessorKey: ColumnAccessorKey.STORABLE_SCHEMA,
          isCollapsible: false,
          isSortable: true,
          sortKey: SortKey.SORT_SCHEMA_NAME,
        },
        render: (storable: Storable) => getArchiveAwareDisplayName(storable.storableSchema),
      },
      {
        column: {
          header: 'Plate type',
          accessorKey: ColumnAccessorKey.PLATE_TYPE,
          isCollapsible: false,
          isSortable: false,
        },
        render: (plate: Plate) => PLATE_SCHEMA_TYPE_TO_TEXT[plate.storableSchema.plateSchemaType],
        isHidden: (storable: Storable) => APIUtil.apiIdToType(storable.id) !== ModelTypeConstants.PLATE,
      },
      {
        column: {
          header: 'Box/Plate dimensions',
          accessorKey: ColumnAccessorKey.GRID_DIMENSIONS,
          isCollapsible: false,
          isSortable: false,
        },
        render: (grid: Grid) => `${grid.storableSchema.height} × ${grid.storableSchema.width}`,
        isHidden: (storable: Storable) =>
          ![ModelTypeConstants.BOX, ModelTypeConstants.PLATE].includes(APIUtil.apiIdToType(storable.id)),
      },
      {
        column: {
          header: 'Total capacity',
          accessorKey: ColumnAccessorKey.TOTAL_CAPACITY,
          isCollapsible: false,
          isSortable: false,
        },
        render: (storable: Grid | Location) =>
          isGridSummary(storable) && storable.storableSchema.plateSchemaType !== PlateSchemaType.FIXED_PLATE
            ? storable.storableSchema.height * storable.storableSchema.width
            : (storableSummaryExtras?.locationCapacityById?.[storable.id]?.totalCapacity ?? ''),
        isHidden: (storable: Storable) =>
          APIUtil.isContainer(storable.id) ||
          (isGridSummary(storable) &&
            APIUtil.isPlate(storable.id) &&
            storable.storableSchema.plateSchemaType === PlateSchemaType.FIXED_PLATE),
      },
      {
        column: {
          header: getQuantityDisplayString(),
          accessorKey: ColumnAccessorKey.VOLUME,
          isCollapsible: false,
          isSortable: false,
        },
        render: (container: Container) => <Measurement measurement={container.volume} />,
        isHidden: (storable: Storable) => APIUtil.apiIdToType(storable.id) !== ModelTypeConstants.CONTAINER,
      },
      {
        column: {
          header: 'Concentration',
          accessorKey: ColumnAccessorKey.CONCENTRATION,
          isCollapsible: false,
          isSortable: false,
        },
        render: (container: Container) => (
          <ContainerConcentration container={container} storableSummaryExtras={storableSummaryExtras} />
        ),
        isHidden: (storable: Storable) => APIUtil.apiIdToType(storable.id) !== ModelTypeConstants.CONTAINER,
      },
    ];

    if (storableSummaryExtras) {
      const renderedStorableSummaryExtras = StorableSummaryExtrasColumns.render(
        storableSummaryExtras,
        storables.map(({id}) => id)
      );
      columns = columns.concat(
        renderedStorableSummaryExtras.columns.map((column: ScrollableTableColumn) => ({
          column,
          render: (storable: Storable) =>
            renderedStorableSummaryExtras.dataById[storable.id][column.accessorKey],
        }))
      );
    }

    if (hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V1)) {
      if (hasMinimumSCRFeatureLevel(SampleControlsFeatureLevel.V2)) {
        columns = columns.concat([
          {
            column: {
              header: 'Restriction access',
              accessorKey: ColumnAccessorKey.SAMPLE_RESTRICTION_ACCESS,
              isCollapsible: false,
              isSortable: false, // TODO(BNCH-53869): enable sorting
            },
            render: (container: Container) => (
              <SampleStatusLabel
                item={container}
                includeAuthorization={true}
                includeAvailability={false}
                renderAllStatuses={true}
                includeMessage={true}
                hideAssigneeInLabel={true}
                itemSupportsSampleStatus={false} // Only used for availability, which is not included
              />
            ),
            isHidden: (storable: Storable) =>
              APIUtil.apiIdToType(storable.id) !== ModelTypeConstants.CONTAINER,
          },
          {
            column: {
              header: getColumnHeaderFromSortKey(SortKey.SORT_SAMPLE_RESTRICTION_STATUS),
              accessorKey: ColumnAccessorKey.SAMPLE_RESTRICTION_STATUS,
              isCollapsible: false,
              isSortable: true,
              sortKey: SortKey.SORT_SAMPLE_RESTRICTION_STATUS,
            },
            render: (container: Container) => <QueryingSampleRestrictionStatusLabel item={container} />,
            isHidden: (storable: Storable) =>
              APIUtil.apiIdToType(storable.id) !== ModelTypeConstants.CONTAINER,
          },
        ]);
      }
      columns = columns.concat([
        {
          column: {
            header: getRestrictedSampleUsersHeaderText(),
            accessorKey: ColumnAccessorKey.RESTRICTED_SAMPLE_USERS,
            isCollapsible: false,
            isSortable: false,
            width: 200,
          },
          render: (container: Container) => (
            <SampleControlRoleUsers users={container.sampleRoles?.restrictedSampleUsers} />
          ),
          isHidden: (storable: Storable) => APIUtil.apiIdToType(storable.id) !== ModelTypeConstants.CONTAINER,
        },
        {
          column: {
            header: 'Sample owners',
            accessorKey: ColumnAccessorKey.SAMPLE_OWNERS,
            isCollapsible: false,
            isSortable: false,
          },
          render: (container: Container) => (
            <SampleControlRoleUsers users={container.sampleRoles?.sampleOwners} />
          ),
          isHidden: (storable: Storable) => APIUtil.apiIdToType(storable.id) !== ModelTypeConstants.CONTAINER,
        },
      ]);
    }

    if (experimentalRolesByContainerId != null) {
      columns.push({
        column: {
          header: 'Role',
          accessorKey: ColumnAccessorKey.EXPERIMENTAL_ROLE,
          isCollapsible: true,
          isSortable: false,
        },
        render: (storable: Storable) =>
          experimentalRolesByContainerId[storable.id] != null ? (
            <RoleDisplay role={experimentalRolesByContainerId[storable.id]!} />
          ) : null,
        isHidden: (storable: Storable) =>
          APIUtil.apiIdToType(storable.id) !== ModelTypeConstants.CONTAINER ||
          !(storable as Container).isFixedPlateWell,
      });
    }

    const tagColumns = ExpandedTableTagUtil.listStorableTagColumns(storables);
    if (selectedContainableTagSchemas && selectedContainableTagSchemas.length >= 1) {
      tagColumns.push(
        ...ExpandedTableTagUtil.listContainableSchemaFieldColumnsGraphQL(selectedContainableTagSchemas)
      );
    }

    if (tagColumns.length) {
      columns = columns.concat(
        tagColumns.map((column: ScrollableTableColumn) => ({
          column,
          render: (storable: Storable) => <StorableTagValue storable={storable} column={column} />,
        }))
      );
    }

    maybeAddWorklistPositionColumn(columns, storables);

    return columns;
  }

  getNonPluginRowContentHeight(
    storable: Storable,
    {storableSummaryExtras, itemStudyAttributes}: StorableListingExtras = {
      storableSummaryExtras: null,
    }
  ): number {
    return Math.max(
      getStudiesRowContentHeight(storable.id, itemStudyAttributes),
      storableSummaryExtras
        ? StorableSummaryExtrasColumns.getRowContentHeight(storableSummaryExtras, storable.id)
        : TABLE_ROW_BODY_HEIGHT,
      ExpandedTableTagUtil.getRowHeight(storable.tags)
    );
  }

  openContextMenu(storable: StorableSummary, e: BasicEvent, extras: unknown): void {
    StorableContextMenu.open(e.nativeEvent, storable);
  }
}

const styles = StyleSheet.create({
  barcodeCellContainer: {display: 'flex'},
  barcodeCellBarcode: {overflow: 'hidden', textOverflow: 'ellipsis', width: '100%'},
});
